import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderKanban, HardDrive, KeyRound, Palette, ScrollText, ShieldCheck, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { ProjectManager } from "@/components/admin/ProjectManager";
import { StorageUsage } from "@/components/admin/StorageUsage";
import { UsersSection } from "@/components/admin/UsersSection";
import { AccessSection } from "@/components/admin/AccessSection";
import { StatusColorsSection } from "@/components/admin/StatusColorsSection";
import { AuditLogSection } from "@/components/admin/AuditLogSection";
import { usePendingUsers } from "@/context/PendingUsersContext";
import { DEPARTMENTS, ROLE_LABELS } from "@/constants/workflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ROLES = Object.keys(ROLE_LABELS);

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [domains, setDomains] = useState("");
  const { version, refresh: refreshPending } = usePendingUsers();

  const load = useCallback(async () => {
    try {
      const [u, p, s] = await Promise.all([
        api.get("/admin/users"),
        api.get("/projects?include_suspended=true"),
        api.get("/admin/settings"),
      ]);
      setUsers(u.data);
      setProjects(p.data);
      setSettings(s.data);
      setDomains((s.data.allowed_email_domains || []).join(", "));
    } catch (error) {
      toast.error(apiError(error));
    }
  }, []);

  useEffect(() => {
    load();
    // A new registration arrives over the admin socket: refresh the list too.
  }, [load, version]);

  const reloadAll = useCallback(() => {
    load();
    refreshPending();
  }, [load, refreshPending]);

  const toggleEditor = async (department, role) => {
    const current = settings.department_editors[department] || [];
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    try {
      const { data } = await api.put("/admin/settings", {
        department_editors: { ...settings.department_editors, [department]: next },
      });
      setSettings({ ...settings, ...data });
      toast.success("Permissions updated");
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const saveDomains = async () => {
    try {
      await api.put("/admin/settings", {
        allowed_email_domains: domains.split(",").map((d) => d.trim()),
      });
      toast.success("Allowed domains updated");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const pendingCount = users.filter((u) => u.approval_status === "pending").length;

  return (
    <div className="min-h-screen bg-[#EEF2F8]">
      <TopBar subtitle="Administration" />
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-6" data-testid="admin-page">
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Administration
        </h1>

        <Tabs defaultValue="users" className="mt-7">
          <TabsList className="okn-scroll h-auto max-w-full flex-nowrap justify-start overflow-x-auto bg-white lg:flex-wrap">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="text-xs">
              <Users size={13} className="mr-1.5" /> Users
              {pendingCount ? (
                <span
                  data-testid="admin-tab-users-badge"
                  className="mono ml-1.5 rounded-full bg-red-600 px-1.5 text-[9px] text-white"
                >
                  {pendingCount}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="access" data-testid="admin-tab-access" className="text-xs">
              <KeyRound size={13} className="mr-1.5" /> Access
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="admin-tab-projects" className="text-xs">
              <FolderKanban size={13} className="mr-1.5" /> Projects
            </TabsTrigger>
            <TabsTrigger value="permissions" data-testid="admin-tab-permissions" className="text-xs">
              <ShieldCheck size={13} className="mr-1.5" /> Status Permissions
            </TabsTrigger>
            <TabsTrigger value="colors" data-testid="admin-tab-colors" className="text-xs">
              <Palette size={13} className="mr-1.5" /> Status Colours
            </TabsTrigger>
            <TabsTrigger value="storage" data-testid="admin-tab-storage" className="text-xs">
              <HardDrive size={13} className="mr-1.5" /> Storage
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit" className="text-xs">
              <ScrollText size={13} className="mr-1.5" /> Audit Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-5">
            <UsersSection users={users} onChanged={reloadAll} />
          </TabsContent>

          <TabsContent value="access" className="mt-5">
            <AccessSection users={users} projects={projects} onChanged={load} />
          </TabsContent>

          <TabsContent value="projects" className="mt-5">
            <ProjectManager projects={projects} onChanged={load} />
          </TabsContent>

          <TabsContent value="colors" className="mt-5">
            {settings ? <StatusColorsSection settings={settings} onChanged={load} /> : null}
          </TabsContent>

          <TabsContent value="storage" className="mt-5">
            <StorageUsage />
          </TabsContent>

          <TabsContent value="audit" className="mt-5">
            <AuditLogSection />
          </TabsContent>

          <TabsContent value="permissions" className="mt-5 space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="mono mb-3 text-[10px] uppercase tracking-wider text-slate-500">
                Allowed registration domains
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="allowed-domains-input"
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                  className="mono border-slate-200 bg-[#EEF2F8] text-xs"
                />
                <Button
                  data-testid="save-domains-button"
                  onClick={saveDomains}
                  className="rounded-full bg-cyan-500 text-xs text-[#04121a]"
                >
                  Save
                </Button>
              </div>
            </div>

            {settings
              ? DEPARTMENTS.map((dept) => (
                  <div
                    key={dept.key}
                    data-testid={`permission-row-${dept.key}`}
                    className="rounded-xl border border-slate-200 bg-white p-5"
                  >
                    <p className="font-display text-sm font-medium">{dept.label} status</p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      Roles allowed to change this department status.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {ROLES.map((role) => (
                        <button
                          key={role}
                          data-testid={`editor-${dept.key}-${role}`}
                          onClick={() => toggleEditor(dept.key, role)}
                          className={`mono rounded-full px-3 py-1 text-[10px] uppercase tracking-wider ${
                            (settings.department_editors[dept.key] || []).includes(role)
                              ? "bg-cyan-500/20 text-cyan-700"
                              : "border border-slate-200 text-slate-500 hover:bg-slate-100"
                          }`}
                        >
                          {ROLE_LABELS[role]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              : null}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
